import { useDisclosure } from '@mantine/hooks';
import type { IObservableArray } from 'mobx';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import type { Recommendation, TTDCampaign } from '@feathr/blackbox';
import { ERecommendationType } from '@feathr/blackbox';
import { useLocalUrl } from '@feathr/extender/state';

interface IUseModalProps {
  /** The Tradedesk campaign */
  campaign: TTDCampaign;
  /** The recommendation models */
  recommendations: IObservableArray<Recommendation>;
  /** Function to set the selected recommendation ID */
  setSelectedRecommendationId: Dispatch<SetStateAction<string | null>>;
}

interface IUseModalReturnProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Function to open the modal of the selected recommendation */
  onOpenModal: (type: ERecommendationType, id: string) => () => void;
  /** Function to close the modal and reset the selected recommendation */
  onCloseModal: () => void;
  /** The recommendation ID in the URL param */
  recommendationIdParam: string | null;
}

/**
 * Hook to manage the modal for the recommendation
 * @param campaign - The campaign
 * @param recommendations - The recommendations collection
 * @param setSelectedRecommendationId - The function to set the selected recommendation ID
 * @returns The modal state and functions to open and close the modal
 */
function useModal({
  campaign,
  recommendations,
  setSelectedRecommendationId,
}: IUseModalProps): IUseModalReturnProps {
  const localUrl = useLocalUrl();
  const history = useHistory();
  const [opened, { close, open }] = useDisclosure(false);

  const params = new URLSearchParams(window.location.search);
  const recommendationIdParam = params.get('recommendation');

  // Pop the modal of the matching recommendation upon page load if a recommendation ID is provided as a URL param
  useEffect(() => {
    if (recommendationIdParam) {
      const matchingRecommendation = recommendations.find(
        (rec) => rec.id === recommendationIdParam,
      );
      if (matchingRecommendation) {
        const type = matchingRecommendation.get('type');
        onOpenModal(type, recommendationIdParam)();
      }
    }
  }, [recommendationIdParam]);

  function onOpenModal(type: ERecommendationType, id: string): () => void {
    return function (): void {
      setSelectedRecommendationId(id);

      if (type === ERecommendationType.UpdateCreatives) {
        const creativesUrl = localUrl(campaign.getItemUrl('edit')) + '#step4';

        history.push(creativesUrl);
      } else {
        open();
      }
    };
  }

  function handleCloseModal(): void {
    close();
    setSelectedRecommendationId(null);
  }

  return {
    opened,
    onOpenModal,
    onCloseModal: handleCloseModal,
    recommendationIdParam,
  };
}

export default useModal;
